import type { ChatSession } from './types';

export type CompletionNotice = Readonly<{
  completionId: string;
  sessionId: string;
  label: string;
}>;

export type CompletionSoundStatus = 'not-prepared' | 'ready' | 'needs-activation' | 'unavailable';

type ClarificationSoundEnvelope = Readonly<{
  status?: unknown;
  client_request_id?: unknown;
}>;

export const HOME_CHAT_COMPLETION_SOUND_STATUS_EVENT = 'home-chat:completion-sound-status';
/**
 * A response-time handoff to the single launcher-owned Home Chat sound
 * dispatcher.  The payload is deliberately only a server-issued durable id:
 * response text and client-created status are never audio evidence.
 */
export const HOME_CHAT_LLM_RESULT_SOUND_EVENT = 'home-chat:llm-result-sound';

/**
 * Deliver a freshly returned, server-authoritative actionable-result id to
 * the launcher reconciler without waiting for its three-second session poll.
 * The reconciler owns page-lifetime de-duplication, so this is not a second
 * audio path and the later durable poll remains silent for the same id.
 */
export function dispatchHomeChatLlmResultSound(eventId: string): void {
  if (typeof window === 'undefined' || !eventId.trim()) return;
  window.dispatchEvent(new CustomEvent(HOME_CHAT_LLM_RESULT_SOUND_EVENT, {
    detail: { eventId },
  }));
}

// Audio contexts are tied to a document, not a component. Keeping this
// instance lets an explicit user gesture unlock it once and lets a later
// background-completion poll play without violating browser autoplay rules.
let completionAudioContext: AudioContext | null = null;
let completionAudioContextConstructor: unknown = null;
let completionAudioElement: HTMLAudioElement | null = null;
let completionSoundStatus: CompletionSoundStatus = 'not-prepared';

const COMPLETION_CHIME_SAMPLE_RATE = 11025;
const COMPLETION_CHIME_DURATION_SECONDS = 0.32;
// A concise alert should be distinct but not startle. Combined with the WAV's
// 0.72 peak, this is about -12 dBFS — aligned with the Web Audio fallback.
const COMPLETION_CHIME_VOLUME = 0.4;

/**
 * Generate a small PCM WAV data URL once per page.  Using an inline source
 * means completion playback has no fetch/cache dependency, while an
 * HTMLAudioElement gives us the browser's actual play() promise rather than
 * treating an oscillator scheduled in a running context as audible output.
 */
function completionChimeDataUrl(): string | null {
  if (typeof window === 'undefined' || typeof window.btoa !== 'function') return null;
  const samples = Math.floor(COMPLETION_CHIME_SAMPLE_RATE * COMPLETION_CHIME_DURATION_SECONDS);
  const dataBytes = samples * 2;
  const bytes = new Uint8Array(44 + dataBytes);
  const view = new DataView(bytes.buffer);
  const writeAscii = (offset: number, text: string) => {
    for (let index = 0; index < text.length; index += 1) bytes[offset + index] = text.charCodeAt(index);
  };
  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(8, 'WAVEfmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, COMPLETION_CHIME_SAMPLE_RATE, true);
  view.setUint32(28, COMPLETION_CHIME_SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(36, 'data');
  view.setUint32(40, dataBytes, true);
  for (let index = 0; index < samples; index += 1) {
    const time = index / COMPLETION_CHIME_SAMPLE_RATE;
    const envelope = Math.min(1, index / 140, (samples - index) / 420);
    const frequency = time < 0.16 ? 784 : 1046;
    const sample = Math.round(Math.sin(2 * Math.PI * frequency * time) * envelope * 0.72 * 32767);
    view.setInt16(44 + (index * 2), sample, true);
  }
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return `data:audio/wav;base64,${window.btoa(binary)}`;
}

function setCompletionSoundStatus(status: CompletionSoundStatus): void {
  if (completionSoundStatus === status) return;
  completionSoundStatus = status;
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(HOME_CHAT_COMPLETION_SOUND_STATUS_EVENT, {
      detail: status,
    }));
  }
}

/** The latest best-effort Web Audio readiness result for the sound control. */
export function getInAppCompletionSoundStatus(): CompletionSoundStatus {
  return completionSoundStatus;
}

function getCompletionAudioContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  const AudioContextCtor = window.AudioContext
    || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextCtor) {
    setCompletionSoundStatus('unavailable');
    return null;
  }
  try {
    if (
      !completionAudioContext
      || completionAudioContext.state === 'closed'
      // Browser engines keep this stable. The guard also ensures a replaced
      // implementation (for example an embed/runtime recovery) cannot retain
      // a stale context from the prior implementation.
      || completionAudioContextConstructor !== AudioContextCtor
    ) {
      completionAudioContext = new AudioContextCtor();
      completionAudioContextConstructor = AudioContextCtor;
    }
    return completionAudioContext;
  } catch {
    setCompletionSoundStatus('unavailable');
    return null;
  }
}

function getCompletionAudioElement(): HTMLAudioElement | null {
  if (typeof document === 'undefined') return null;
  try {
    if (!completionAudioElement) {
      const source = completionChimeDataUrl();
      if (!source) {
        setCompletionSoundStatus('unavailable');
        return null;
      }
      const element = document.createElement('audio');
      element.preload = 'auto';
      element.src = source;
      element.volume = 1;
      completionAudioElement = element;
    }
    return completionAudioElement;
  } catch {
    setCompletionSoundStatus('unavailable');
    return null;
  }
}

function rewindAudio(element: HTMLAudioElement): void {
  try { element.pause(); } catch { /* a failed pause never prevents playback */ }
  try { element.currentTime = 0; } catch { /* source is still loading; play() decides readiness */ }
}

type MediaPlaybackResult = 'played' | 'needs-activation' | 'unavailable';

function playbackFailureResult(error: unknown): MediaPlaybackResult {
  return (error as { name?: unknown })?.name === 'NotAllowedError'
    ? 'needs-activation'
    : 'unavailable';
}

/**
 * Start an inline media chime and wait for the browser's observable playback
 * result. `prime` runs from an explicit gesture at near-zero volume so a
 * later background completion has an activated media element without adding a
 * surprise test chime when the preference is enabled.
 */
async function playMediaCompletionChime(prime = false): Promise<MediaPlaybackResult> {
  const element = getCompletionAudioElement();
  if (!element) return 'unavailable';
  rewindAudio(element);
  element.muted = false;
  element.volume = prime ? 0.001 : COMPLETION_CHIME_VOLUME;
  try {
    const playResult = element.play();
    // Supported browsers return a promise. Without one there is no observable
    // playback result, so fall through to the Web Audio fallback instead of
    // claiming a silent/no-op media element delivered a chime.
    if (!playResult || typeof playResult.then !== 'function') return 'unavailable';
    await playResult;
    if (prime) rewindAudio(element);
    element.muted = false;
    element.volume = COMPLETION_CHIME_VOLUME;
    return 'played';
  } catch (error) {
    rewindAudio(element);
    element.muted = false;
    element.volume = COMPLETION_CHIME_VOLUME;
    return playbackFailureResult(error);
  }
}

// Keep this state check behind a function boundary. TypeScript otherwise
// narrows `context.state` after the early return below and cannot model the
// state transition caused asynchronously by `context.resume()`.
function isAudioContextRunning(context: AudioContext): boolean {
  return context.state === 'running';
}

/**
 * Detect one server-owned successful completion. Per-tab de-duplication is
 * supplied by the stable caller-owned Set so re-renders and repeated polls
 * cannot repeat a notice. No local fetch failure or missing activity implies
 * completion.
 */
export function completionNoticeFromTransition(
  _previous: ChatSession | undefined,
  current: ChatSession,
  activeSessionId: string | null,
  deliveredCompletionIds: ReadonlySet<string>,
): CompletionNotice | null {
  const completion = current.completion;
  if (
    !completion
    || completion.state !== 'completed'
    || !completion.unseen
    || current.session_id === activeSessionId
    || deliveredCompletionIds.has(completion.completion_id)
  ) return null;
  return {
    completionId: completion.completion_id,
    sessionId: current.session_id,
    label: completion.label,
  };
}

/**
 * Sound is an opt-in completion cue, not a duplicate visual toast. It must
 * therefore notice a new successful completion even when that session is
 * currently open (where the visual Ready/toast is intentionally suppressed).
 */
export function completionSoundIdFromTransition(
  current: ChatSession,
  deliveredCompletionIds: ReadonlySet<string>,
): string | null {
  const completion = current.completion;
  if (
    !completion
    || completion.state !== 'completed'
    || deliveredCompletionIds.has(completion.completion_id)
  ) return null;
  return completion.completion_id;
}

/**
 * Return only newly observed, server-issued actionable-result IDs. The caller
 * owns the page-lifetime set: neither selected-session state, card text,
 * timestamps, nor a polling transition are evidence that a result is new.
 */
export function llmResultEventSoundIdsFromSession(
  current: ChatSession,
  deliveredEventIds: ReadonlySet<string>,
): string[] {
  const events = current.llm_result_events;
  if (!events?.length) return [];
  const ids = new Set<string>();
  for (const event of events) {
    if (deliveredEventIds.has(event.event_id) || ids.has(event.event_id)) continue;
    ids.add(event.event_id);
  }
  return Array.from(ids);
}

/**
 * Return a one-per-dispatch id for a freshly returned clarification card.
 * A persisted/hydrated card has no client dispatch id and must stay silent:
 * it is not a new result, even if the component remounts or polls again.
 */
export function clarificationSoundIdFromEnvelope<T extends ClarificationSoundEnvelope>(
  envelope: T,
  _sessionId: string | null | undefined,
  dispatchRequestId?: string,
): string | null {
  if (
    envelope.status !== 'clarification_needed'
    && envelope.status !== 'topic_clarification_needed'
  ) return null;
  const requestId = typeof envelope.client_request_id === 'string'
    && envelope.client_request_id.trim()
    ? envelope.client_request_id
    : dispatchRequestId;
  if (!requestId) return null;
  return `${envelope.status === 'topic_clarification_needed' ? 'topic-clarification' : 'clarification'}:${requestId}`;
}

/**
 * Must be called from a user gesture. Browsers generally leave a context
 * created by a background polling callback suspended, which looks like a
 * successful call but produces no audible result.
 */
export async function prepareInAppCompletionSound(): Promise<boolean> {
  const mediaResult = await playMediaCompletionChime(true);
  if (mediaResult === 'played') {
    setCompletionSoundStatus('ready');
    return true;
  }
  const context = getCompletionAudioContext();
  if (!context) {
    setCompletionSoundStatus(mediaResult);
    return false;
  }
  if (isAudioContextRunning(context)) {
    setCompletionSoundStatus('ready');
    return true;
  }
  try {
    await context.resume();
    // A fulfilled resume() promise alone is not an audible guarantee: some
    // browser/device states resolve it while retaining a suspended context.
    // Verify the observable state so later completion polling never silently
    // treats an inaudible chime as delivered.
    const ready = isAudioContextRunning(context);
    setCompletionSoundStatus(ready ? 'ready' : 'needs-activation');
    return ready;
  } catch {
    setCompletionSoundStatus(mediaResult === 'unavailable' ? 'unavailable' : 'needs-activation');
    return false;
  }
}

/** Optional, explicitly enabled sound for a genuine new completion. */
export async function tryInAppCompletionSound(): Promise<boolean> {
  const mediaResult = await playMediaCompletionChime();
  if (mediaResult === 'played') {
    setCompletionSoundStatus('ready');
    return true;
  }
  const context = getCompletionAudioContext();
  // If the browser still requires user activation, retain the visible in-app
  // alert and Ready marker instead of pretending an inaudible tone played.
  if (!context) {
    setCompletionSoundStatus(mediaResult);
    return false;
  }
  if (!isAudioContextRunning(context)) {
    // A resumable but suspended context means a new gesture may still unlock
    // sound, even if this browser did not expose a usable media element.
    setCompletionSoundStatus('needs-activation');
    return false;
  }
  try {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    // A single full-scale oscillator is attenuated to -13 dBFS. This is over
    // 15 dB louder than the original 0.035 gain while retaining ample
    // headroom for browser/device processing and avoiding clipping.
    gain.gain.setValueAtTime(0.22, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.24);
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.24);
    setCompletionSoundStatus('ready');
    return true;
  } catch {
    setCompletionSoundStatus(mediaResult === 'needs-activation' ? 'needs-activation' : 'unavailable');
    return false;
  }
}
